/**
 * UserRelation 类型和 Schema 定义
 *
 * ⚠️ 注意: 此文件由脚本自动生成，请勿手动修改
 * 如需更新，请运行代码生成脚本
 */
import type { SchemaDefinition } from './_schema'

/**
 * UserRelation
 */
export interface UserRelation {
  /** 创建时间（UTC） */
  created_at?: string
  /** 更新时间（UTC） */
  updated_at?: string
  /** 主键（UUID4），全局唯一，不可重复，36位 */
  id?: string
  /** 发起方用户ID */
  src_id?: string
  /** 目标方用户ID */
  dst_id?: string
  /** 是否关注 */
  is_following?: boolean
  /** 是否好友 */
  is_friend?: boolean
  /** 是否拉黑 */
  is_blocked?: boolean
  /** 是否屏蔽消息 */
  is_muted?: boolean
  /** 关注时间（UTC） */
  followed_at?: string
  /** 成为好友时间（UTC） */
  friended_at?: string
  /** 拉黑时间（UTC） */
  blocked_at?: string
  /** 屏蔽消息时间（UTC） */
  muted_at?: string
  /** 互动次数（聊天、评论、点赞等） */
  interaction_count?: number
  /** 最后互动时间（UTC） */
  last_interaction_at?: string
  /** 对战次数 */
  battle_count?: number
  /** 对战胜利次数 */
  battle_win_count?: number
  /** 对战失败次数 */
  battle_lose_count?: number
  /** 对战平局次数 */
  battle_draw_count?: number
  /** 首次对战时间（UTC） */
  first_battled_at?: string
  /** 最后对战时间（UTC） */
  last_battled_at?: string
  /** 作为队友次数 */
  battle_companion_count?: number
  /** 作为对手次数 */
  battle_enemy_count?: number
  /** 首次作为队友时间（UTC） */
  first_battle_companioned_at?: string
  /** 首次作为对手时间（UTC） */
  first_battle_enemied_at?: string
  /** 亲密度等级 */
  intimacy_level?: number
  /** 亲密度积分 */
  intimacy_points?: number
  /** 用户备注名称 */
  remark_name?: string
  /** 用户备注内容 */
  remark_content?: string
  /** 备注时间（UTC） */
  remarked_at?: string
}

/**
 * UserRelation Schema
 */
export const UserRelationSchema: SchemaDefinition = {
  properties: {
    created_at: {
      description: '创建时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '创建时间',
    },
    updated_at: {
      description: '更新时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '更新时间',
    },
    id: {
      description: '主键（UUID4），全局唯一，不可重复，36位',
      format: 'string',
      type: 'string',
      label: '主键',
    },
    src_id: {
      description: '发起方用户ID',
      format: 'string',
      type: 'string',
      label: '发起方用户ID',
    },
    dst_id: {
      description: '目标方用户ID',
      format: 'string',
      type: 'string',
      label: '目标方用户ID',
    },
    is_following: {
      description: '是否关注',
      format: 'bool',
      type: 'boolean',
      label: '是否关注',
    },
    is_friend: {
      description: '是否好友',
      format: 'bool',
      type: 'boolean',
      label: '是否好友',
    },
    is_blocked: {
      description: '是否拉黑',
      format: 'bool',
      type: 'boolean',
      label: '是否拉黑',
    },
    is_muted: {
      description: '是否屏蔽消息',
      format: 'bool',
      type: 'boolean',
      label: '是否屏蔽消息',
    },
    followed_at: {
      description: '关注时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '关注时间',
    },
    friended_at: {
      description: '成为好友时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '成为好友时间',
    },
    blocked_at: {
      description: '拉黑时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '拉黑时间',
    },
    muted_at: {
      description: '屏蔽消息时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '屏蔽消息时间',
    },
    interaction_count: {
      description: '互动次数（聊天、评论、点赞等）',
      format: 'int',
      type: 'integer',
      label: '互动次数',
    },
    last_interaction_at: {
      description: '最后互动时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '最后互动时间',
    },
    battle_count: {
      description: '对战次数',
      format: 'int',
      type: 'integer',
      label: '对战次数',
    },
    battle_win_count: {
      description: '对战胜利次数',
      format: 'int',
      type: 'integer',
      label: '对战胜利次数',
    },
    battle_lose_count: {
      description: '对战失败次数',
      format: 'int',
      type: 'integer',
      label: '对战失败次数',
    },
    battle_draw_count: {
      description: '对战平局次数',
      format: 'int',
      type: 'integer',
      label: '对战平局次数',
    },
    first_battled_at: {
      description: '首次对战时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '首次对战时间',
    },
    last_battled_at: {
      description: '最后对战时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '最后对战时间',
    },
    battle_companion_count: {
      description: '作为队友次数',
      format: 'int',
      type: 'integer',
      label: '作为队友次数',
    },
    battle_enemy_count: {
      description: '作为对手次数',
      format: 'int',
      type: 'integer',
      label: '作为对手次数',
    },
    first_battle_companioned_at: {
      description: '首次作为队友时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '首次作为队友时间',
    },
    first_battle_enemied_at: {
      description: '首次作为对手时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '首次作为对手时间',
    },
    intimacy_level: {
      description: '亲密度等级',
      format: 'int',
      type: 'integer',
      label: '亲密度等级',
    },
    intimacy_points: {
      description: '亲密度积分',
      format: 'int',
      type: 'integer',
      label: '亲密度积分',
    },
    remark_name: {
      description: '用户备注名称',
      format: 'string',
      type: 'string',
      label: '用户备注名称',
    },
    remark_content: {
      description: '用户备注内容',
      format: 'string',
      type: 'string',
      label: '用户备注内容',
    },
    remarked_at: {
      description: '备注时间（UTC）',
      format: '*time.Time',
      type: 'string',
      label: '备注时间',
    },
  },
  type: 'object',
}
